/* ============================================================
 * TMT Shell — Announcement bar, mobile menu, scroll-blur, cart badge
 *
 * Vanilla JS, no dependencies.
 * All hooks use [data-tmt-*] attributes so markup and JS stay loosely
 * coupled. Safe to load on pages that don't have every hook present.
 * ============================================================ */
(function () {
  'use strict';

  var ANNOUNCEMENT_STORAGE_KEY = 'tmt:announcement:dismissed';
  var MENU_OPEN_CLASS = 'is-open';
  var SCROLL_LOCK_CLASS = 'tmt-scroll-lock';
  var HEADER_SCROLLED_CLASS = 'is-scrolled';
  var SCROLL_THRESHOLD = 10;

  function sanitizeImageSrc(value) {
    if (!value) return null;
    try {
      var parsed = new URL(value, window.location.href);
      var protocol = parsed.protocol;
      if (protocol === 'http:' || protocol === 'https:') {
        return parsed.href;
      }
    } catch (e) {
      return null;
    }
    return null;
  }

  /* ---------- Announcement bar dismiss ---------- */
  function initAnnouncementBar() {
    var bar = document.querySelector('[data-tmt-announcement]');
    if (!bar) return;

    var dismissId = bar.getAttribute('data-tmt-announcement') || 'default';
    var storageKey = ANNOUNCEMENT_STORAGE_KEY + ':' + dismissId;

    try {
      if (localStorage.getItem(storageKey) === '1') {
        bar.hidden = true;
        return;
      }
    } catch (e) { /* storage disabled — still show the bar */ }

    var closeBtn = bar.querySelector('[data-tmt-announcement-close]');
    if (!closeBtn) return;

    closeBtn.addEventListener('click', function () {
      bar.hidden = true;
      try { localStorage.setItem(storageKey, '1'); } catch (e) {}
    });
  }

  /* ---------- Mobile menu ----------
   *
   * Nuclear bind strategy. The hamburger toggle was breaking when ANY
   * schema setting was added to the header (logo OR primary menu). Root
   * cause analysis showed that Shopify's image_tag emits an <img> with
   * width="520" (matching image_url width), and the wrapping <a> uses
   * inline-flex+flex-shrink:0 — so on mobile the link grows past its
   * grid column and visually overlaps the burger button. Since the link
   * comes after the burger in DOM order and has no z-index, the link
   * captures the click and navigates home instead of toggling the menu.
   *
   * Defenses (in order of precedence):
   *   1. Capture-phase listener bound directly to every toggle — fires
   *      BEFORE any bubbling click on an overlapping element.
   *   2. stopImmediatePropagation — stops any other capture-phase
   *      handler (Dawn's HeaderDrawer/MenuDrawer custom elements) from
   *      consuming the event.
   *   3. MutationObserver re-binds after Shopify Theme Editor section
   *      re-renders so a re-rendered burger picks up handlers without
   *      a page reload.
   *   4. dataset.tmtBound guard prevents double-binding.
   *   5. Document-level delegation as a last-resort fallback.
   */
  function initMobileMenu() {
    function getMenu() { return document.querySelector('[data-tmt-menu]'); }
    function getOverlay() { return document.querySelector('[data-tmt-menu-overlay]'); }

    function isOpen() {
      var menu = getMenu();
      return !!menu && menu.classList.contains(MENU_OPEN_CLASS);
    }

    function open() {
      var menu = getMenu();
      if (!menu) return;
      var overlay = getOverlay();
      menu.classList.add(MENU_OPEN_CLASS);
      if (overlay) overlay.classList.add(MENU_OPEN_CLASS);
      document.querySelectorAll('[data-tmt-menu-toggle]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'true');
      });
      menu.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);
      document.body.classList.add(SCROLL_LOCK_CLASS);
    }

    function close() {
      var menu = getMenu();
      if (!menu) return;
      var overlay = getOverlay();
      menu.classList.remove(MENU_OPEN_CLASS);
      if (overlay) overlay.classList.remove(MENU_OPEN_CLASS);
      document.querySelectorAll('[data-tmt-menu-toggle]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      menu.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      document.body.classList.remove(SCROLL_LOCK_CLASS);
    }

    function bindToggles() {
      document.querySelectorAll('[data-tmt-menu-toggle]').forEach(function (btn) {
        if (btn.dataset.tmtBound === '1') return;
        btn.dataset.tmtBound = '1';
        btn.addEventListener(
          'click',
          function (evt) {
            evt.preventDefault();
            evt.stopPropagation();
            evt.stopImmediatePropagation();
            if (isOpen()) close(); else open();
          },
          true /* capture phase — runs before any bubbling click handler */
        );
      });
    }

    function bindCloseHooks() {
      var overlay = getOverlay();
      if (overlay && overlay.dataset.tmtBound !== '1') {
        overlay.dataset.tmtBound = '1';
        overlay.addEventListener('click', close);
      }
      var menu = getMenu();
      if (menu && menu.dataset.tmtBound !== '1') {
        menu.dataset.tmtBound = '1';
        menu.addEventListener('click', function (evt) {
          var closer = evt.target.closest('[data-tmt-menu-close]');
          if (closer && isOpen()) close();
        });
      }
    }

    /* Document-level fallback delegation — guarantees the toggle works
       even if direct binding ever misses due to rapid section reorder. */
    document.addEventListener('click', function (evt) {
      var toggle = evt.target.closest('[data-tmt-menu-toggle]');
      if (!toggle) return;
      if (toggle.dataset.tmtBound === '1') return; /* direct handler covers it */
      evt.preventDefault();
      evt.stopPropagation();
      if (isOpen()) close(); else open();
    }, true);

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && isOpen()) close();
    });

    /* Initial bind + initial ARIA state */
    bindToggles();
    bindCloseHooks();
    var initialMenu = getMenu();
    if (initialMenu) initialMenu.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-tmt-menu-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });

    /* Re-bind after Shopify Theme Editor section re-renders. The editor
       replaces .shopify-section subtrees on settings change — the new
       toggle node has no listener until we re-bind. */
    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function () {
        bindToggles();
        bindCloseHooks();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ---------- Sticky header scroll-blur ---------- */
  function initStickyHeader() {
    var header = document.querySelector('[data-tmt-header]');
    if (!header) return;

    var ticking = false;
    function update() {
      if (window.scrollY > SCROLL_THRESHOLD) {
        header.classList.add(HEADER_SCROLLED_CLASS);
      } else {
        header.classList.remove(HEADER_SCROLLED_CLASS);
      }
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Cart icon badge count ---------- */
  function updateCartBadge(count) {
    var badges = document.querySelectorAll('[data-tmt-cart-count]');
    badges.forEach(function (el) {
      el.textContent = String(count);
      if (count > 0) {
        el.hidden = false;
        el.removeAttribute('hidden');
      } else {
        el.hidden = true;
      }
    });
  }

  function fetchCartCount() {
    if (!window.fetch) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) updateCartBadge(cart.item_count || 0); })
      .catch(function () { /* silent */ });
  }

  function initCartBadge() {
    var badge = document.querySelector('[data-tmt-cart-count]');
    if (!badge) return;

    // Listen for a custom event that the cart drawer fires after add/remove/update
    document.addEventListener('tmt:cart:update', function (evt) {
      var count = evt && evt.detail && typeof evt.detail.item_count === 'number'
        ? evt.detail.item_count
        : null;
      if (count !== null) {
        updateCartBadge(count);
      } else {
        fetchCartCount();
      }
    });

    // Fetch on page load in case the SSR value is stale (e.g. BFCache)
    window.addEventListener('pageshow', function (evt) {
      if (evt.persisted) fetchCartCount();
    });
  }

  /* ---------- Scroll spy + smooth-scroll anchors ---------- */
  /* Activated by [data-tmt-scroll-spy] on a nav container whose child
   * [data-tmt-scroll-spy-link] links point to in-page anchors (#id) or
   * absolute URLs. Internal anchors get smooth-scroll with an offset
   * (data-tmt-scroll-offset, default 140) and the currently-in-view
   * section's link is flagged is-active + aria-current="true". */
  function initScrollSpy() {
    var containers = document.querySelectorAll('[data-tmt-scroll-spy]');
    if (!containers.length) return;

    containers.forEach(function (container) {
      var offset = parseInt(container.getAttribute('data-tmt-scroll-offset'), 10);
      if (isNaN(offset)) offset = 140;

      var links = container.querySelectorAll('[data-tmt-scroll-spy-link]');
      if (!links.length) return;

      var anchors = [];
      links.forEach(function (link) {
        var href = link.getAttribute('href') || '';
        var isExternal = link.getAttribute('data-tmt-scroll-spy-link') === 'external' || href.indexOf('#') !== 0;
        if (!isExternal) anchors.push({ id: href, link: link });

        link.addEventListener('click', function (evt) {
          if (isExternal) return;
          var target = document.querySelector(href);
          if (!target) return;
          evt.preventDefault();
          var y = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        });
      });

      if (!anchors.length) return;

      var ticking = false;
      function update() {
        var current = anchors[0];
        for (var i = 0; i < anchors.length; i++) {
          var el = document.querySelector(anchors[i].id);
          if (!el) continue;
          if (el.getBoundingClientRect().top <= 200) current = anchors[i];
        }
        anchors.forEach(function (a) {
          var isActive = a === current;
          a.link.classList.toggle('is-active', isActive);
          if (isActive) {
            a.link.setAttribute('aria-current', 'true');
          } else {
            a.link.removeAttribute('aria-current');
          }
        });
        ticking = false;
      }

      function onScroll() {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      }

      update();
      window.addEventListener('scroll', onScroll, { passive: true });
    });
  }

  /* ---------- Quick-add to cart ---------- */
  /* Activated by [data-tmt-quick-add] on a button with data-tmt-variant-id.
   * Posts to /cart/add.js and fires tmt:cart:update so the badge refreshes.
   * Dispatches tmt:cart:open to let the cart drawer show itself. */
  function initQuickAdd() {
    var buttons = document.querySelectorAll('[data-tmt-quick-add]');
    if (!buttons.length) return;
    if (!window.fetch) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (evt) {
        var variantId = btn.getAttribute('data-tmt-variant-id');
        if (!variantId) return;
        evt.preventDefault();

        if (btn.disabled) return;
        var originalText = btn.textContent;
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = 'Adding…';

        var card = btn.closest('article') || btn.closest('[data-tmt-card]');
        var subCheck = card ? card.querySelector('[data-tmt-subscribe-check]') : null;
        var sellingPlan = (subCheck && subCheck.checked) ? subCheck.getAttribute('data-plan-id') : null;
        var payload = { id: variantId, quantity: 1 };
        if (sellingPlan) payload.selling_plan = sellingPlan;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        })
          .then(function (res) {
            if (!res.ok) throw new Error('Quick-add failed: ' + res.status);
            return res.json();
          })
          .then(function () {
            // Refresh the cart badge and refresh+open the drawer so it shows the newly-added item
            document.dispatchEvent(new CustomEvent('tmt:cart:update', { detail: {} }));
            if (window.TMTCart && typeof window.TMTCart.refresh === 'function') {
              window.TMTCart.refresh().then(function () {
                if (typeof window.TMTCart.open === 'function') window.TMTCart.open();
              });
            } else {
              document.dispatchEvent(new CustomEvent('tmt:cart:open'));
            }
            btn.textContent = 'Added ✓';
            setTimeout(function () {
              btn.textContent = originalText;
              btn.disabled = false;
              btn.removeAttribute('aria-busy');
            }, 1200);
          })
          .catch(function () {
            btn.textContent = 'Try again';
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            setTimeout(function () { btn.textContent = originalText; }, 2000);
          });
      });
    });
  }

  /* ---------- Shop All subscribe-toggle price swap ----------
   * Activated by [data-tmt-subscribe-check]. On change, swaps the price
   * displayed in the same article between data-onetime-price and
   * data-sub-price. The ATC click handler reads the same checkbox to
   * decide whether to send a selling_plan. */
  function initSubscribeToggles() {
    var boxes = document.querySelectorAll('[data-tmt-subscribe-check]');
    if (!boxes.length) return;
    boxes.forEach(function (box) {
      var card = box.closest('article') || box.closest('[data-tmt-card]');
      if (!card) return;
      var priceEl = card.querySelector('[data-tmt-card-price]');
      if (!priceEl) return;
      var onetime = box.getAttribute('data-onetime-price') || priceEl.textContent;
      var sub = box.getAttribute('data-sub-price') || onetime;
      function paint() {
        priceEl.textContent = box.checked ? sub : onetime;
      }
      paint();
      box.addEventListener('change', paint);
    });
  }

  /* ---------- Flip cards (ACQ Homepage tea collection) ---------- */
  /* Activated by [data-tmt-flip-trigger] buttons. Toggles aria-pressed;
   * the corresponding CSS rotates the inner face with `.preserve-3d`.
   * Keyboard-accessible by virtue of being <button>. */
  function initFlipCards() {
    var triggers = document.querySelectorAll('[data-tmt-flip-trigger]');
    if (!triggers.length) return;

    triggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var pressed = btn.getAttribute('aria-pressed') === 'true';
        btn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      });
    });
  }

  /* ---------- Horizontal carousel (no-op scaffolding) ----------
   * Testimonial carousels on ACQ Homepage use CSS scroll-snap for the
   * base behavior. This function is a hook for future enhancements
   * (prev/next buttons, keyboard nav) without forcing JS dependency
   * on carousels that work without it.
   * Looks for [data-tmt-carousel][data-tmt-carousel-controls] and wires
   * prev/next buttons if present. */
  function initCarousels() {
    var carousels = document.querySelectorAll('[data-tmt-carousel][data-tmt-carousel-controls]');
    if (!carousels.length) return;

    carousels.forEach(function (track) {
      var prev = document.querySelector('[data-tmt-carousel-prev="' + track.id + '"]');
      var next = document.querySelector('[data-tmt-carousel-next="' + track.id + '"]');
      if (!prev && !next) return;

      function step(dir) {
        var first = track.querySelector(':scope > *');
        if (!first) return;
        var w = first.getBoundingClientRect().width + 24;
        track.scrollBy({ left: dir * w, behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
    });
  }

  /* ---------- PDP Add-to-Cart (Tea / Set / Teaware) ----------
   * Intercepts submits on any product form marked data-type="add-to-cart-form",
   * POSTs to /cart/add.js, then opens the cart drawer via window.TMTCart.
   * Falls back to dispatching tmt:cart:open if TMTCart isn't mounted yet. */
  function initPdpAtc() {
    if (!window.fetch) return;
    var forms = document.querySelectorAll('form[data-type="add-to-cart-form"][id^="tmt-"]');
    if (!forms.length) return;

    forms.forEach(function (form) {
      if (form.__tmtAtcBound) return;
      form.__tmtAtcBound = true;

      form.addEventListener('submit', function (evt) {
        evt.preventDefault();

        var btn = form.querySelector('button[type="submit"][name="add"]');
        var originalText = btn ? btn.innerHTML : '';
        if (btn) {
          if (btn.disabled) return;
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        }

        var formData = new FormData(form);
        formData.set('sections_url', window.location.pathname);

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: formData
        })
          .then(function (res) {
            if (!res.ok) {
              return res.json().then(function (err) {
                throw new Error((err && err.description) || 'Add to cart failed');
              });
            }
            return res.json();
          })
          .then(function () {
            document.dispatchEvent(new CustomEvent('tmt:cart:update', { detail: {} }));
            if (window.TMTCart && typeof window.TMTCart.refresh === 'function') {
              window.TMTCart.refresh().then(function () {
                if (typeof window.TMTCart.open === 'function') window.TMTCart.open();
              });
            } else {
              document.dispatchEvent(new CustomEvent('tmt:cart:open'));
            }
          })
          .catch(function (err) {
            console.error('[TMTPdpAtc]', err);
          })
          .finally(function () {
            if (btn) {
              btn.disabled = false;
              btn.removeAttribute('aria-busy');
              btn.innerHTML = originalText;
            }
          });
      });
    });
  }

  /* ---------- Product gallery — thumbnail click-to-switch ----------
   * Activated on any [data-tmt-gallery] container with child
   * [data-tmt-gallery-main] wrapping the large <img> and sibling
   * [data-tmt-gallery-thumb] buttons. Each thumb carries the full-size
   * image URL in data-full-src (and optional data-full-alt).
   * Clicking a thumb swaps the main img's src + alt, clears srcset so
   * the browser honours the explicit src, and toggles .is-active +
   * aria-pressed across the thumbs in that gallery. */
  function initGallery() {
    var galleries = document.querySelectorAll('[data-tmt-gallery]');
    if (!galleries.length) return;

    galleries.forEach(function (gallery) {
      var mainWrap = gallery.querySelector('[data-tmt-gallery-main]');
      if (!mainWrap) return;
      var mainImg = mainWrap.querySelector('img');
      if (!mainImg) return;

      var thumbs = gallery.querySelectorAll('[data-tmt-gallery-thumb]');
      if (!thumbs.length) return;

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var fullSrc = thumb.getAttribute('data-full-src');
          if (!fullSrc) return;
          var safeFullSrc = sanitizeImageSrc(fullSrc);
          if (!safeFullSrc) return;
          mainImg.removeAttribute('srcset');
          mainImg.src = safeFullSrc;
          var alt = thumb.getAttribute('data-full-alt');
          if (alt !== null) mainImg.alt = alt;

          thumbs.forEach(function (t) {
            var isActive = t === thumb;
            t.classList.toggle('is-active', isActive);
            t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
        });
      });

      /* Prev/next arrow scrolling. The track scrolls by one thumbnail
       * width + gap (64 + 8 = 72px). Arrows hide at the start/end of
       * the scroll range so the UI signals there's nothing further. */
      var track = gallery.querySelector('[data-tmt-gallery-thumbs-track]');
      var prev = gallery.querySelector('[data-tmt-gallery-prev]');
      var next = gallery.querySelector('[data-tmt-gallery-next]');
      if (!track || (!prev && !next)) return;

      var SCROLL_STEP = 72; // 64px thumb + 8px gap

      function updateArrows() {
        var maxScroll = track.scrollWidth - track.clientWidth;
        var overflowing = maxScroll > 1;
        if (prev) prev.hidden = !overflowing || track.scrollLeft <= 1;
        if (next) next.hidden = !overflowing || track.scrollLeft >= maxScroll - 1;
      }

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollBy({ left: -SCROLL_STEP, behavior: 'smooth' });
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          track.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' });
        });
      }

      track.addEventListener('scroll', updateArrows, { passive: true });
      // Recompute on resize — thumb overflow can flip when the column width
      // changes at responsive breakpoints.
      window.addEventListener('resize', updateArrows);

      // Initial state — defer past layout so scrollWidth is accurate.
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(updateArrows);
      } else {
        setTimeout(updateArrows, 0);
      }
    });
  }

  /* ---------- Purchase option radio toggle (PDP / Bundle) ----------
   * The tmt-purchase-option snippet renders each option as a <label>
   * wrapping a real <input type="radio">. Radios toggle natively, but
   * two things don't wire themselves:
   *   1. The visual .is-selected class on the <label> (the filled
   *      radio-dot + accent border key off this class, NOT :checked).
   *   2. Shopify's cart add needs a <input name="selling_plan"> on the
   *      form when a subscription option is picked, and no such input
   *      when one-time is picked.
   * This handler listens for radio changes within any form containing
   * [data-tmt-purchase-option-input], updates the sibling labels, and
   * manages the hidden selling_plan input on the form.
   */
  function initPurchaseOption() {
    var radios = document.querySelectorAll('[data-tmt-purchase-option-input]');
    if (!radios.length) return;

    function syncForm(form) {
      if (!form) return;
      // Update .is-selected on all option labels in this form
      var labels = form.querySelectorAll('[data-tmt-purchase-option]');
      labels.forEach(function (label) {
        var inp = label.querySelector('[data-tmt-purchase-option-input]');
        label.classList.toggle('is-selected', !!(inp && inp.checked));
      });

      // Find the currently checked radio and read its selling plan ID
      var checked = form.querySelector('[data-tmt-purchase-option-input]:checked');
      var planId = checked ? (checked.getAttribute('data-selling-plan-id') || '') : '';

      // Ensure there's exactly one hidden [name="selling_plan"] input;
      // remove it entirely when no plan (one-time) so Shopify doesn't
      // receive an empty selling_plan key.
      var existing = form.querySelector('input[name="selling_plan"]');
      if (planId) {
        if (!existing) {
          existing = document.createElement('input');
          existing.type = 'hidden';
          existing.name = 'selling_plan';
          form.appendChild(existing);
        }
        existing.value = planId;
      } else if (existing) {
        existing.parentNode.removeChild(existing);
      }

      /* Mirror the selected option's displayed price into any ATC
       * button price span ([data-tmt-atc-price]) inside this form, so
       * switching between one-time and subscribe & save updates the
       * "Add Bundle to Cart — $XX" text on the button. Each option
       * label already renders its own price via the snippet's
       * .tmt-purchase-option__price element — we just copy its text. */
      var priceTargets = form.querySelectorAll('[data-tmt-atc-price]');
      if (priceTargets.length && checked) {
        var checkedLabel = checked.closest('[data-tmt-purchase-option]');
        var priceEl = checkedLabel ? checkedLabel.querySelector('.tmt-purchase-option__price') : null;
        var priceTxt = priceEl ? (priceEl.textContent || '').trim() : '';
        if (priceTxt) {
          priceTargets.forEach(function (el) { el.textContent = priceTxt; });
        }
      }

      /* Update the bundle bottom CTA button price (lives in a different
       * section/form). The button stores both one-time and subscription
       * prices as data attributes; we swap the visible price text based
       * on whether a selling plan is selected (planId truthy = subscription). */
      var ctaPriceEls = document.querySelectorAll('[data-tmt-bundle-cta-price]');
      if (ctaPriceEls.length) {
        var isSubscription = !!planId;
        ctaPriceEls.forEach(function (el) {
          var btn = el.closest('[data-tmt-bundle-cta-button]') || el.parentElement;
          if (!btn) return;
          var newPrice = isSubscription
            ? btn.getAttribute('data-sub-price')
            : btn.getAttribute('data-onetime-price');
          if (newPrice) el.textContent = newPrice;
        });
      }
    }

    // Sync on load (respects server-rendered `checked` + `.is-selected`)
    var forms = new Set();
    radios.forEach(function (r) { if (r.form) forms.add(r.form); });
    forms.forEach(syncForm);

    // Sync on change
    radios.forEach(function (r) {
      r.addEventListener('change', function () { syncForm(r.form); });
    });
  }

  /* ---------- Build Your Ritual (RET BYR) ----------
   * Wires the tea selector, order summary, and primary CTA sections on
   * the RET Build Your Ritual page. Event delegation on [data-tmt-byr]
   * handles card toggles / qty steppers; summary + CTA hooks are queried
   * page-wide because those sections are siblings to the selector.
   *
   * Posts to /cart/add.js with multi-line items on CTA click (includes
   * selling_plan when subscription is active) and fires tmt:cart:update
   * + tmt:cart:open so the shell cart badge refreshes and the drawer
   * opens. Does NOT redirect to /cart. */
  function initBYR() {
    var byrRoot = document.querySelector('[data-tmt-byr]');
    if (!byrRoot) return;
    if (!window.fetch) return;

    var PRICE_ONE = parseFloat(byrRoot.getAttribute('data-tmt-byr-price-one')) || 39;
    var PRICE_SUB = parseFloat(byrRoot.getAttribute('data-tmt-byr-price-sub')) || 29;

    var summaryRoot = document.querySelector('[data-tmt-byr-summary-root]');
    var MIN_TEAS = 3;
    var FREE_SHIP = 59;
    if (summaryRoot) {
      var minAttr = summaryRoot.getAttribute('data-tmt-byr-min');
      var shipAttr = summaryRoot.getAttribute('data-tmt-byr-free-ship');
      if (minAttr) MIN_TEAS = parseInt(minAttr, 10) || MIN_TEAS;
      if (shipAttr) FREE_SHIP = parseFloat(shipAttr) || FREE_SHIP;
    }

    // selections: keyed by variant ID (or slug fallback) → qty + metadata
    var selections = {};
    // Read initial purchaseType from the server-rendered .is-selected toggle
    // so the page's default (RET = subscription, ACQ = one-time) survives the
    // first render pass. The toggles live in the summary section, which is a
    // sibling of byrRoot, so we query from document rather than byrRoot.
    var preSelected = document.querySelector('[data-tmt-byr-purchase].is-selected');
    var purchaseType = preSelected
      ? preSelected.getAttribute('data-tmt-byr-purchase')
      : 'subscription';
    var frequency = 1;

    function unitPrice() {
      return purchaseType === 'subscription' ? PRICE_SUB : PRICE_ONE;
    }

    function totalQty() {
      var n = 0;
      for (var k in selections) {
        if (selections.hasOwnProperty(k)) n += selections[k].qty;
      }
      return n;
    }

    function cardKey(card) {
      return card.getAttribute('data-tmt-variant-id') || card.getAttribute('data-tmt-byr-slug');
    }

    function currentQty(card) {
      var key = cardKey(card);
      return (key && selections[key]) ? selections[key].qty : 0;
    }

    function setQty(card, qty) {
      var key = cardKey(card);
      if (!key) return;
      if (qty <= 0) {
        delete selections[key];
      } else {
        var nameEl = card.querySelector('.tmt-byr-card__name');
        selections[key] = {
          qty: qty,
          slug: card.getAttribute('data-tmt-byr-slug') || '',
          variantId: card.getAttribute('data-tmt-variant-id') || '',
          sellingPlanId: card.getAttribute('data-tmt-selling-plan-id') || '',
          teaName: nameEl ? (nameEl.textContent || '').trim() : ''
        };
      }
      updateCardUI(card);
      renderSummary();
    }

    function updateCardUI(card) {
      var qty = currentQty(card);
      var selected = qty > 0;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');

      var badge = card.querySelector('.tmt-byr-card__qty-badge');
      if (badge) {
        badge.hidden = !selected;
        var badgeQty = badge.querySelector('[data-tmt-byr-qty]');
        if (badgeQty) badgeQty.textContent = String(qty);
      }

      var addBtn = card.querySelector('[data-tmt-byr-toggle]');
      var stepper = card.querySelector('[data-tmt-byr-stepper]');
      if (addBtn) addBtn.hidden = selected;
      if (stepper) {
        stepper.hidden = !selected;
        var stepQty = stepper.querySelector('[data-tmt-byr-stepper-qty]');
        if (stepQty) stepQty.textContent = String(qty);
      }
    }

    function setText(selector, text) {
      var nodes = document.querySelectorAll(selector);
      for (var i = 0; i < nodes.length; i++) nodes[i].textContent = text;
    }

    function renderSummary() {
      var count = totalQty();
      var oneTimeSubtotal = count * PRICE_ONE;
      var subtotal = count * unitPrice();
      var savings = oneTimeSubtotal - subtotal;
      var hasMin = count >= MIN_TEAS;
      var empty = count === 0;
      var shippingFree = subtotal >= FREE_SHIP;
      var shippingRemaining = Math.max(0, FREE_SHIP - subtotal);

      // Header
      setText('[data-tmt-byr-summary-eyebrow]', empty ? 'Getting started' : 'Your ritual');
      setText(
        '[data-tmt-byr-summary-title]',
        empty
          ? 'Pick three teas to build your ritual.'
          : count + ' ' + (count === 1 ? 'tea' : 'teas') + ' selected'
      );

      // Progress nudge
      var showProgress = !empty && !hasMin;
      document.querySelectorAll('[data-tmt-byr-progress]').forEach(function (el) {
        el.hidden = !showProgress;
      });
      if (showProgress) {
        var remaining = MIN_TEAS - count;
        var strongTxt = 'Add ' + remaining + ' more ' + (remaining === 1 ? 'tea' : 'teas');
        setText('[data-tmt-byr-progress-strong]', strongTxt);
      }

      // Purchase toggle pressed state
      document.querySelectorAll('[data-tmt-byr-purchase]').forEach(function (btn) {
        var isSel = btn.getAttribute('data-tmt-byr-purchase') === purchaseType;
        btn.classList.toggle('is-selected', isSel);
        btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });

      // Frequency wrap only when subscription
      document.querySelectorAll('[data-tmt-byr-frequency-wrap]').forEach(function (el) {
        el.hidden = purchaseType !== 'subscription';
      });

      // Tea card price emphasis swap
      var isOne = purchaseType === 'onetime';
      document.querySelectorAll('[data-tmt-byr-tea]').forEach(function (card) {
        card.classList.toggle('is-one-time', isOne);
      });

      // Breakdown lines
      setText('[data-tmt-byr-count]', String(count));
      setText('[data-tmt-byr-count-word]', count === 1 ? 'tea' : 'teas');
      setText('[data-tmt-byr-summary-subtotal]', empty ? '—' : ('$' + oneTimeSubtotal.toFixed(2)));

      var savingsLines = document.querySelectorAll('[data-tmt-byr-savings-line]');
      var showSavings = purchaseType === 'subscription' && !empty;
      savingsLines.forEach(function (el) { el.hidden = !showSavings; });
      setText('[data-tmt-byr-summary-savings]', '−$' + savings.toFixed(2));

      var shippingTxt;
      if (empty) shippingTxt = '—';
      else if (shippingFree) shippingTxt = 'FREE';
      else if (shippingRemaining > 0) shippingTxt = '$' + shippingRemaining.toFixed(2) + ' to free';
      else shippingTxt = 'Calculated at checkout';
      setText('[data-tmt-byr-summary-shipping]', shippingTxt);

      // Total
      setText(
        '[data-tmt-byr-total-label]',
        purchaseType === 'subscription' ? 'Total per shipment' : 'Total'
      );
      setText('[data-tmt-byr-summary-total]', '$' + subtotal.toFixed(2));

      // CTA label + disabled state (applies to summary + primary-cta buttons)
      var ctaLabel;
      if (count === 0) {
        ctaLabel = 'Pick three teas to continue';
      } else if (!hasMin) {
        var r = MIN_TEAS - count;
        ctaLabel = 'Add ' + r + ' more ' + (r === 1 ? 'tea' : 'teas') + ' to continue';
      } else if (purchaseType === 'subscription') {
        ctaLabel = 'Start My Subscription · $' + subtotal.toFixed(2);
      } else {
        ctaLabel = 'Add to Cart · $' + subtotal.toFixed(2);
      }
      setText('[data-tmt-byr-atc-label]', ctaLabel);
      document.querySelectorAll('[data-tmt-byr-atc]').forEach(function (btn) {
        btn.disabled = !hasMin;
        btn.setAttribute('aria-disabled', !hasMin ? 'true' : 'false');
      });

      // ── Incentive section (ACQ only; absent on RET) ─────────
      // Toggles the locked ↔ unlocked visual state, swaps eyebrow /
      // headline / body copy via [data-locked] + [data-unlocked]
      // attributes, reveals the summary's "Free teaware unlocked"
      // line, and forces shipping to FREE when unlocked.
      var incentive = document.querySelector('[data-tmt-byr-incentive-section]');
      if (incentive) {
        var incentiveMin = parseInt(incentive.getAttribute('data-tmt-byr-incentive-min'), 10);
        if (isNaN(incentiveMin)) incentiveMin = 3;
        var unlocked = count >= incentiveMin;
        incentive.classList.toggle('is-unlocked', unlocked);
        incentive.classList.toggle('is-locked', !unlocked);

        function swapCopy(el, replaceRemaining) {
          if (!el) return;
          var key = unlocked ? 'data-unlocked' : 'data-locked';
          var txt = el.getAttribute(key);
          if (txt === null) return;
          if (replaceRemaining) {
            var rem = Math.max(0, incentiveMin - count);
            txt = txt.replace('{remaining}', String(rem));
          }
          el.textContent = txt;
        }
        swapCopy(incentive.querySelector('[data-tmt-byr-incentive-eyebrow]'), false);
        swapCopy(incentive.querySelector('[data-tmt-byr-incentive-headline]'), false);
        swapCopy(incentive.querySelector('[data-tmt-byr-incentive-body]'), true);

        // Summary "Free teaware unlocked" line (ACQ summary only)
        document.querySelectorAll('[data-tmt-byr-incentive-line]').forEach(function (el) {
          el.hidden = !unlocked;
        });

        // Force FREE shipping when incentive is unlocked (ACQ: free
        // shipping is driven by the incentive gate, not the $59 threshold).
        if (unlocked) {
          setText('[data-tmt-byr-summary-shipping]', 'FREE');
        }
      }
    }

    /* Card interactions — delegated on the selector root */
    byrRoot.addEventListener('click', function (evt) {
      var card = evt.target.closest('[data-tmt-byr-tea]');
      if (!card || !byrRoot.contains(card)) return;

      var toggleBtn = evt.target.closest('[data-tmt-byr-toggle]');
      var incBtn = evt.target.closest('[data-tmt-byr-inc]');
      var decBtn = evt.target.closest('[data-tmt-byr-dec]');

      if (toggleBtn) {
        evt.preventDefault();
        var qty = currentQty(card);
        /* Toggle: qty 0 → 1 (select), qty > 0 → 0 (deselect).
         * MIN_TEAS is the checkout-gate minimum, not a selection cap,
         * so there is no upper bound on total qty. */
        if (qty === 0) {
          setQty(card, 1);
        } else {
          setQty(card, 0);
        }
      } else if (incBtn) {
        evt.preventDefault();
        /* Increment: no upper cap. */
        setQty(card, currentQty(card) + 1);
      } else if (decBtn) {
        evt.preventDefault();
        setQty(card, Math.max(0, currentQty(card) - 1));
      }
    });

    /* Purchase toggle (sub / one-time) — buttons live in the summary section */
    document.querySelectorAll('[data-tmt-byr-purchase]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var next = btn.getAttribute('data-tmt-byr-purchase');
        if (next && next !== purchaseType) {
          purchaseType = next;
          renderSummary();
        }
      });
    });

    /* Frequency select */
    var freqSelect = document.querySelector('[data-tmt-byr-frequency]');
    if (freqSelect) {
      freqSelect.addEventListener('change', function () {
        frequency = parseInt(freqSelect.value, 10) || 1;
      });
    }

    /* CTA click — post line items to /cart/add.js, dispatch cart events */
    function handleAtcClick() {
      if (totalQty() < MIN_TEAS) return;

      var items = [];
      for (var key in selections) {
        if (!selections.hasOwnProperty(key)) continue;
        var sel = selections[key];
        var vid = parseInt(sel.variantId, 10);
        if (!vid) continue; // skip teas without a variant ID wired
        var item = { id: vid, quantity: sel.qty };
        if (purchaseType === 'subscription' && sel.sellingPlanId) {
          item.selling_plan = parseInt(sel.sellingPlanId, 10);
        }
        item.properties = {
          '_byr_ritual': 'true',
          '_byr_purchase_type': purchaseType
        };
        if (purchaseType === 'subscription') {
          item.properties['_byr_frequency_months'] = String(frequency);
        }
        items.push(item);
      }

      if (!items.length) {
        console.warn('[TMTByr] No variants wired on selected tea cards — cannot add to cart.');
        return;
      }

      var atcBtns = document.querySelectorAll('[data-tmt-byr-atc]');
      atcBtns.forEach(function (b) {
        b.disabled = true;
        b.setAttribute('aria-busy', 'true');
      });
      setText('[data-tmt-byr-atc-label]', 'Adding…');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw new Error((err && err.description) || 'BYR add failed: ' + res.status);
            });
          }
          return res.json();
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('tmt:cart:update', { detail: {} }));
          setText('[data-tmt-byr-atc-label]', 'Redirecting…');
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          console.error('[TMTByr]', err);
          setText('[data-tmt-byr-atc-label]', 'Try again');
          setTimeout(function () {
            atcBtns.forEach(function (b) { b.removeAttribute('aria-busy'); });
            renderSummary();
          }, 2000);
        });
    }

    /* NOTE: ATC click is handled by the inline <script> at the bottom of
     * sections/tmt-byr-selector.liquid (a single authoritative checkout
     * handler keyed on [data-tmt-byr-checkout]). Binding here as well
     * would cause /cart/add.js to fire twice on each click. */

    /* Initial render — idempotent; reflects the default empty state */
    renderSummary();
  }

  /* ---------- Cart gift guard ----------
   * Free teaware gift is added with line property _gift: "true" from the ACQ
   * quiz results page (Option 2) and the ACQ Build Your Ritual page. An
   * auto-discount makes it $0. Prerequisite: cart must contain at least N
   * individual teas (by total qty across the configured tea handles). If a
   * customer drops below N teas, the gift is auto-removed so they can't keep
   * it for free without meeting the threshold.
   *
   * Config is read from <script type="application/json" id="tmt-cart-gift-config">
   * in layout/theme.tmt.liquid so handles / threshold can be edited without
   * touching JS. Hard-coded fallback is used if the config block is missing. */
  var DEFAULT_GIFT_CONFIG = {
    teaHandles: [
      'achieve-clarity-tea',
      'discover-joy-tea',
      'seek-peace-tea',
      'have-hope-tea',
      'enjoy-rest-tea',
      'find-strength-tea'
    ],
    giftHandle: 'tea-brewing-kit-gift-90-tea-bags-1-perfect-scoop-1',
    minTeas: 3
  };
  var giftConfig = null;
  var isValidating = false;

  function readGiftConfig() {
    if (giftConfig) return giftConfig;
    var node = document.getElementById('tmt-cart-gift-config');
    if (node && node.textContent) {
      try {
        var parsed = JSON.parse(node.textContent);
        giftConfig = {
          teaHandles: Array.isArray(parsed.teaHandles) && parsed.teaHandles.length ? parsed.teaHandles : DEFAULT_GIFT_CONFIG.teaHandles,
          giftHandle: typeof parsed.giftHandle === 'string' && parsed.giftHandle ? parsed.giftHandle : DEFAULT_GIFT_CONFIG.giftHandle,
          minTeas: typeof parsed.minTeas === 'number' && parsed.minTeas > 0 ? parsed.minTeas : DEFAULT_GIFT_CONFIG.minTeas
        };
        return giftConfig;
      } catch (err) {
        console.warn('[TMTCartGiftGuard] config parse failed, using defaults', err);
      }
    }
    giftConfig = DEFAULT_GIFT_CONFIG;
    return giftConfig;
  }

  function inspectCart(cart, cfg) {
    var teaQty = 0;
    var giftLineKey = null;
    if (!cart || !cart.items) return { teaQty: 0, giftLineKey: null };
    cart.items.forEach(function (item) {
      if (cfg.teaHandles.indexOf(item.handle) !== -1) {
        teaQty += item.quantity;
      }
      var isGiftByProp = item.properties && (item.properties._gift === 'true' || item.properties._gift === true);
      if (item.handle === cfg.giftHandle || isGiftByProp) {
        giftLineKey = item.key;
      }
    });
    return { teaQty: teaQty, giftLineKey: giftLineKey };
  }

  function isCartPage() {
    var p = (window.location && window.location.pathname) || '';
    return p === '/cart' || p.indexOf('/cart?') === 0 || p.indexOf('/cart/') === 0;
  }

  function refreshCartUI() {
    /* On the dedicated /cart page, the cart drawer's refresh logic doesn't
     * own the visible UI — Dawn's main-cart-items section does. Easiest
     * way to repaint everything (item rows, totals, footer) is a reload.
     * The reload happens after the /cart/change.js call resolves, so the
     * server-rendered page reflects the removed gift. */
    if (isCartPage()) {
      window.location.reload();
      return Promise.resolve();
    }
    if (window.TMTCart && typeof window.TMTCart.refresh === 'function') {
      return window.TMTCart.refresh();
    }
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('tmt:cart:update', {
          detail: {
            item_count: cart.item_count,
            total_price: cart.total_price,
            items_subtotal_price: cart.items_subtotal_price,
            cart: cart
          }
        }));
      });
  }

  function validateCartGift(cart) {
    /* Re-entrancy guard: removing the gift triggers another cart update,
     * which re-fires tmt:cart:update. Without this flag we'd race the
     * server and could double-POST /cart/change.js. */
    if (isValidating) return;
    var cfg = readGiftConfig();

    var run = function (c) {
      var info = inspectCart(c, cfg);
      if (info.teaQty < cfg.minTeas && info.giftLineKey) {
        isValidating = true;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: info.giftLineKey, quantity: 0 })
        })
          .then(function (r) { return r.json(); })
          .then(function () { return refreshCartUI(); })
          .catch(function (err) { console.error('[TMTCartGiftGuard] remove failed', err); })
          .finally(function () { isValidating = false; });
      }
    };

    if (cart && cart.items) {
      run(cart);
    } else {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(run)
        .catch(function () { /* silent */ });
    }
  }

  function initCartGiftGuard() {
    document.addEventListener('tmt:cart:update', function (evt) {
      var cart = evt && evt.detail && evt.detail.cart;
      validateCartGift(cart);
    });
    document.addEventListener('tmt:cart:open', function () {
      validateCartGift(null);
    });

    /* MutationObserver fallback for /cart page. Dawn's CartItems custom
     * element re-renders its innerHTML after every quantity/remove change.
     * The primary signal is the tmt:cart:update event we now dispatch from
     * assets/cart.js, but this observer is a belt-and-suspenders backup in
     * case that bridge ever stops firing (e.g. a future Dawn refactor).
     * Debounced so the burst of mutations during a single re-render only
     * triggers one validation. */
    if (isCartPage()) {
      var bindObserver = function () {
        var target = document.querySelector('cart-items') || document.querySelector('.cart-items') || document.querySelector('main');
        if (!target || !window.MutationObserver) return;
        var debounceTimer = null;
        var observer = new MutationObserver(function () {
          if (isValidating) return;
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(function () { validateCartGift(null); }, 500);
        });
        observer.observe(target, { childList: true, subtree: true });
      };
      if (document.readyState !== 'loading') {
        bindObserver();
      } else {
        document.addEventListener('DOMContentLoaded', bindObserver);
      }
    }

    /* Initial check on load — covers BFCache restores and direct landings
     * with stale cart state (e.g. user removed teas in another tab). */
    if (document.readyState !== 'loading') {
      validateCartGift(null);
    } else {
      document.addEventListener('DOMContentLoaded', function () { validateCartGift(null); });
    }
  }

  /* ---------- Boot ---------- */
  function init() {
    initAnnouncementBar();
    initMobileMenu();
    initStickyHeader();
    initCartBadge();
    initCartGiftGuard();
    initScrollSpy();
    initQuickAdd();
    initSubscribeToggles();
    initPdpAtc();
    initGallery();
    initPurchaseOption();
    initBYR();
    initFlipCards();
    initCarousels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose a small API for other scripts (cart drawer, etc.)
  window.TMTShell = {
    updateCartBadge: updateCartBadge,
    fetchCartCount: fetchCartCount
  };
})();
